/**
 * CaseStudyCard - Summary card that scrolls to a full case study on the page.
 *
 * The "Read More" button smooth-scrolls to the element whose id matches
 * `targetId`. Target elements should set scroll-margin-top to clear the
 * sticky site header.
 */
import React from 'react';

export interface CaseStudyCardProps {
  title: string;
  description?: string;
  tags?: string[];
  targetId: string;
  readMoreLabel?: string;
  icon?: React.ReactNode;
  className?: string;
}

const CaseStudyCard: React.FC<CaseStudyCardProps> = (props) => {
  const {
    title,
    description,
    tags = [],
    targetId,
    readMoreLabel = 'Read More',
    icon,
    className = '',
  } = props;

  const scrollToStudy = () => {
    document.getElementById(targetId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className={`feature-card ${className}`} role="article" aria-label={title} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      {icon && (
        <div className="icon-box icon-box-lg" style={{ marginBottom: 'var(--spacing-5)' }} aria-hidden="true">
          {icon}
        </div>
      )}
      <h3 style={{ fontSize: 'var(--font-size-xl)', fontWeight: 'var(--font-weight-bold)', color: 'var(--color-text)', marginBottom: 'var(--spacing-3)' }}>
        {title}
      </h3>
      {tags.length > 0 && (
        <div style={{ display: 'flex', gap: 'var(--spacing-2)', flexWrap: 'wrap', justifyContent: 'center', marginBottom: 'var(--spacing-4)' }}>
          {tags.map((tag) => (
            <span key={tag} className="badge badge-primary">{tag}</span>
          ))}
        </div>
      )}
      {description && (
        <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)', lineHeight: 'var(--line-height-relaxed)', marginBottom: 'var(--spacing-6)', flex: 1 }}>
          {description}
        </p>
      )}
      <button type="button" className="btn btn-primary" onClick={scrollToStudy} aria-label={`Read the ${title} case study`}>
        {readMoreLabel}
      </button>
    </div>
  );
};

export default CaseStudyCard;
